
import { toString as toString_1, Record, Union } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { SimGraph_tryFind, SimulationComponent_$reflection, SimulationComponentState_$reflection, FastAlgExp, expToKatex, getAlgExpWidth, FastAlgExp_$reflection, FastData_$reflection } from "./SimGraphTypes.js";
import { class_type, lambda_type, unit_type, string_type, option_type, tuple_type, list_type, bool_type, bigint_type, uint32_type, record_type, int32_type, array_type, union_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { toString } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { toText, interpolate, printf, toFail } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { item, getSubArray } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { NumberBase_$reflection, WaveIndexT_$reflection, Connection_$reflection, Component_$reflection, LoadedComponent_$reflection, Port_$reflection, ComponentType_$reflection } from "../Common/CommonTypes.js";
import { createAtom, equals, comparePrimitives, stringHash, compare, int32ToString } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { tryPick as tryPick_1, singleton, sortBy, collect, append, exists as exists_1, fold, choose, filter, map as map_1, sort, reverse, empty, cons, foldBack, head, tail, isEmpty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { toArray, count as count_1, fold as fold_1, ofList, change, exists, containsKey, tryPick, empty as empty_1, toList, tryFind } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { empty as empty_2, add, contains } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { MapModule_ValuesA, MapModule_KeysA, StringModule_Concat } from "../Common/EEExtensions.js";
import { min } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { LookupArray$1_$reflection } from "../Common/LookupArray.js";
import { fcToInt } from "../Common/Ids.js";
import { sprintInitial } from "../Common/Helpers.js";

export class FData extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Data", "Alg"];
    }
}

export function FData_$reflection() {
    return union_type("SimTypes.FData", [], FData, () => [[["Item", FastData_$reflection()]], [["Item", FastAlgExp_$reflection()]]]);
}

export function FData__get_Width(this$) {
    if (this$.tag === 1) {
        return getAlgExpWidth(this$.fields[0]) | 0;
    }
    else {
        return this$.fields[0].Width | 0;
    }
}

export function FData__get_fdToString(this$) {
    if (this$.tag === 1) {
        return expToKatex(this$.fields[0]);
    }
    else if (this$.fields[0].Dat.tag === 1) {
        return toString(this$.fields[0].Dat.fields[0]);
    }
    else {
        return this$.fields[0].Dat.fields[0].toString();
    }
}

export function FData__get_toExp(this$) {
    if (this$.tag === 0) {
        return new FastAlgExp(/* DataLiteral */ 1, [this$.fields[0]]);
    }
    else {
        return this$.fields[0];
    }
}

export function FData__get_toFastData(this$) {
    if (this$.tag === 0) {
        return this$.fields[0];
    }
    else {
        return toFail(printf("Expected data, found Alg FData"));
    }
}

/**
 * Wrapper to allow arrays to be resized for longer simulations while keeping the links between inputs
 * and outputs
 */
export class StepArray$1 extends Record {
    constructor(Step, Index) {
        super();
        this.Step = Step;
        this.Index = (Index | 0);
    }
}

export function StepArray$1_$reflection(gen0) {
    return record_type("SimTypes.StepArray`1", [gen0], StepArray$1, () => [["Step", array_type(gen0)], ["Index", int32_type]]);
}

/**
 * This type represents an array of time steps of simulation data.
 * In any simulation, for a given IOArray, only one of the three step stores will be used.
 * For (very strong) efficiency reasons this cannot be implemented as a disjoint union:
 * the code that reads and writes IOArray elements will access the appropriate store.
 * Truthtable simulations use FDataStep everywhere.
 * Normal simulations use the UInt32 or BigInt store according to the size of the relevant bus.
 * 
 * The uint32 and bigint stores are REGIONS OF SHARED SLABS, not arrays of their own: step s of
 * this IO lives at `slab[StepBase + s]`, and `StepLength` steps belong to it. FastCreate's
 * arena packs every step region of a build into a few large slabs (under both runtimes), so a
 * design has dozens of step allocations rather than one per output port, and a port's data is
 * named by an integer offset. Always go through the members below - indexing a slab without
 * adding StepBase reads another port's data, which no bounds check will ever catch.
 */
export class IOArray extends Record {
    constructor(FDataStep, UInt32Slab, BigIntSlab, StepBase, StepLength, Width, Index) {
        super();
        this.FDataStep = FDataStep;
        this.UInt32Slab = UInt32Slab;
        this.BigIntSlab = BigIntSlab;
        this.StepBase = (StepBase | 0);
        this.StepLength = (StepLength | 0);
        this.Width = (Width | 0);
        this.Index = (Index | 0);
    }
}

export function IOArray_$reflection() {
    return record_type("SimTypes.IOArray", [], IOArray, () => [["FDataStep", array_type(FData_$reflection())], ["UInt32Slab", array_type(uint32_type)], ["BigIntSlab", array_type(bigint_type)], ["StepBase", int32_type], ["StepLength", int32_type], ["Width", int32_type], ["Index", int32_type]]);
}

/**
 * the whole uint32 region as a fresh array - a copy, for cold extraction paths only;
 * empty when this IO is not on the uint32 path, as the store itself once was
 */
export function IOArray__get_U32Contents(this$) {
    if (this$.UInt32Slab.length === 0) {
        return new Uint32Array(0);
    }
    else {
        return getSubArray(this$.UInt32Slab, this$.StepBase, this$.StepLength);
    }
}

/**
 * the whole bigint region as a fresh array - a copy, for cold extraction paths only;
 * empty when this IO is not on the bigint path
 */
export function IOArray__get_BigContents(this$) {
    if (this$.BigIntSlab.length === 0) {
        return new Array(0);
    }
    else {
        return getSubArray(this$.BigIntSlab, this$.StepBase, this$.StepLength);
    }
}

/**
 * Where one clock step sits in the circular simulation arrays: the step number itself, its
 * index into the arrays, and the index of the step before it. All three follow from the step
 * number, so the simulation loop works them out once per step and hands the same value to
 * every component - numStep % maxArraySize is an integer division, and it used to be redone
 * for every component of every step. A struct so that passing it costs nothing.
 */
export class StepIndex extends Record {
    constructor(NumStep, SimStep, SimStepOld) {
        super();
        this.NumStep = (NumStep | 0);
        this.SimStep = (SimStep | 0);
        this.SimStepOld = (SimStepOld | 0);
    }
}

export function StepIndex_$reflection() {
    return record_type("SimTypes.StepIndex", [], StepIndex, () => [["NumStep", int32_type], ["SimStep", int32_type], ["SimStepOld", int32_type]]);
}

/**
 * Used for efficiency reasons.
 * For a given normal simulation these arrays show whether the corresponding
 * component input or output is a bigint or a unint32 type bus, and therefore
 * show IOArray array is used for the data.
 */
export class BigIntState extends Record {
    constructor(InputIsBigInt, OutputIsBigInt) {
        super();
        this.InputIsBigInt = InputIsBigInt;
        this.OutputIsBigInt = OutputIsBigInt;
    }
}

export function BigIntState_$reflection() {
    return record_type("SimTypes.BigIntState", [], BigIntState, () => [["InputIsBigInt", array_type(bool_type)], ["OutputIsBigInt", array_type(bool_type)]]);
}

/**
 * FastComponent represents a physical component in a simulation. Because sheets can be
 * instantiated in multiple places a given sheet component can have multiple FastComponents
 * in the simulation.
 * Arrays on FastComponent are filled up with simulation data per clock step as a clocked
 * simulation progresses.
 * Equality is by reference: a FastComponent is a mutable object with an identity, holding
 * step arrays that can run to megabytes, so comparing two of them field by field would be
 * both meaningless and ruinous. It also carries its own reducers, which are functions and
 * so have no structural equality at all.
 */
export class FastComponent extends Record {
    constructor(fId, cId, FType, State, Active, UseBigInt, BigIntState, InputLinks, InputDrivers, Outputs, SimComponent, AccessPath, FullName, FLabel, ReduceComb, ReduceClocked, Index, OutLinks, CustomInLinks, CustomOutIndex, CustomOutPort, Touched, DrivenComponents, NumMissingInputValues, VerilogOutputName, VerilogComponentName) {
        super();
        this.fId = fId;
        this.cId = cId;
        this.FType = FType;
        this.State = State;
        this.Active = Active;
        this.UseBigInt = UseBigInt;
        this.BigIntState = BigIntState;
        this.InputLinks = InputLinks;
        this.InputDrivers = InputDrivers;
        this.Outputs = Outputs;
        this.SimComponent = SimComponent;
        this.AccessPath = AccessPath;
        this.FullName = FullName;
        this.FLabel = FLabel;
        this.ReduceComb = ReduceComb;
        this.ReduceClocked = ReduceClocked;
        this.Index = Index;
        this.OutLinks = OutLinks;
        this.CustomInLinks = CustomInLinks;
        this.CustomOutIndex = CustomOutIndex;
        this.CustomOutPort = (CustomOutPort | 0);
        this.Touched = Touched;
        this.DrivenComponents = DrivenComponents;
        this.NumMissingInputValues = (NumMissingInputValues | 0);
        this.VerilogOutputName = VerilogOutputName;
        this.VerilogComponentName = VerilogComponentName;
    }
}

export function FastComponent_$reflection() {
    return record_type("SimTypes.FastComponent", [], FastComponent, () => [["fId", tuple_type(int32_type, list_type(int32_type))], ["cId", int32_type], ["FType", ComponentType_$reflection()], ["State", option_type(StepArray$1_$reflection(SimulationComponentState_$reflection()))], ["Active", bool_type], ["UseBigInt", bool_type], ["BigIntState", option_type(BigIntState_$reflection())], ["InputLinks", array_type(IOArray_$reflection())], ["InputDrivers", array_type(option_type(tuple_type(int32_type, int32_type)))], ["Outputs", array_type(IOArray_$reflection())], ["SimComponent", SimulationComponent_$reflection()], ["AccessPath", list_type(int32_type)], ["FullName", string_type], ["FLabel", string_type], ["ReduceComb", lambda_type(StepIndex_$reflection(), unit_type)], ["ReduceClocked", lambda_type(StepIndex_$reflection(), unit_type)], ["Index", int32_type], ["OutLinks", array_type(array_type(tuple_type(int32_type, int32_type)))], ["CustomInLinks", array_type(int32_type)], ["CustomOutIndex", int32_type], ["CustomOutPort", int32_type], ["Touched", bool_type], ["DrivenComponents", list_type(FastComponent_$reflection())], ["NumMissingInputValues", int32_type], ["VerilogOutputName", array_type(string_type)], ["VerilogComponentName", string_type]]);
}

/**
 * for debugging - get a short usually unique truncation of the fId
 */
export function FastComponent__get_ShortId(this$) {
    return int32ToString(this$.fId[0]);
}

/**
 * Convenience array used so that waveform simulation can access
 * component outputs (drivers) without a Map lookup
 */
export class Driver extends Record {
    constructor(Index, DriverWidth, DriverData) {
        super();
        this.Index = Index;
        this.DriverWidth = (DriverWidth | 0);
        this.DriverData = DriverData;
    }
}

export function Driver_$reflection() {
    return record_type("SimTypes.Driver", [], Driver, () => [["Index", int32_type], ["DriverWidth", int32_type], ["DriverData", IOArray_$reflection()]]);
}

/**
 * Type used to tie component ports to simulation data
 * for advanved wavefor simulation features.
 */
export class SheetPort extends Record {
    constructor(Sheet, PortOnComp) {
        super();
        this.Sheet = Sheet;
        this.PortOnComp = PortOnComp;
    }
}

export function SheetPort_$reflection() {
    return record_type("SimTypes.SheetPort", [], SheetPort, () => [["Sheet", string_type], ["PortOnComp", Port_$reflection()]]);
}

/**
 * The design a simulation was built FROM, as opposed to the simulation built from it.
 * 
 * **Everything here is the size of the design somebody drew.** A design of seven sheets is seven
 * sheets here however many instances it expands to - and a design that expands to 49,152 sheet
 * instances is exactly the case this exists for. The renderer needs the design to draw a
 * selector, a hierarchy and a set of names; it does not need the expansion, and holding one to
 * get at the other is what made the renderer's memory grow with a simulation running elsewhere.
 * 
 * The instance queries below are the point of the type. An instance is an `InstancePath`, a list
 * of the custom-component ids entered on the way down to it, and every question anyone asks about
 * one - which sheet is it a copy of, what is it labelled, what is inside it - is answered by
 * walking that path through the DESIGN. Each step is a lookup in one sheet's components, so a
 * query costs the depth of the path and the size of a sheet, and never the expansion. They used
 * to be answered from maps built over every instance in the simulation: 208,896 entries on
 * largeTest, for questions only ever asked about the handful of instances on screen.
 * 
 * The design the simulation was BUILT from, not the one on the canvas now. An edit to the
 * schematic is meant to change nothing in a running waveform simulation until it is restarted or
 * refreshed, and reading the live project instead moved half the selector and left the rest.
 */
export class SimulatedDesign extends Record {
    constructor(DesignSheets, DesignAsDrawn, DesignTopSheet, DesignComponentsById, DesignConnectionsByPort) {
        super();
        this.DesignSheets = DesignSheets;
        this.DesignAsDrawn = DesignAsDrawn;
        this.DesignTopSheet = DesignTopSheet;
        this.DesignComponentsById = DesignComponentsById;
        this.DesignConnectionsByPort = DesignConnectionsByPort;
    }
}

export function SimulatedDesign_$reflection() {
    return record_type("SimTypes.SimulatedDesign", [], SimulatedDesign, () => [["DesignSheets", list_type(LoadedComponent_$reflection())], ["DesignAsDrawn", list_type(LoadedComponent_$reflection())], ["DesignTopSheet", string_type], ["DesignComponentsById", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [string_type, class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, Component_$reflection()])])], ["DesignConnectionsByPort", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [SheetPort_$reflection(), list_type(Connection_$reflection())])]]);
}

/**
 * The custom component that introduces an instance, or None for the top sheet, which nothing
 * introduces. A step off the near end of the path: the head IS that component, and the tail
 * says which sheet it is drawn on.
 */
export function SimulatedDesign__CustomOfInstance_1A26531(this$, _arg) {
    const ap = _arg;
    if (!isEmpty(ap)) {
        const option_1 = tryFind(SimulatedDesign__SheetOfInstance_1A26531(this$, tail(ap)), this$.DesignComponentsById);
        if (option_1 != null) {
            return tryFind(head(ap), option_1);
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}

/**
 * The design-time name of the sheet an instance is a copy of: what the user called it, and
 * what they see in the Sheets menu.
 * 
 * Walked from the top sheet down, each id read as a custom component on the sheet reached so
 * far - so foldBack, the path being innermost first. A path that does not lead anywhere in
 * this design - one left over from a simulation of an earlier version of it - stops at the
 * sheet it got to, which is the same answer the expansion-sized version gave for an instance
 * it did not hold.
 */
export function SimulatedDesign__SheetOfInstance_1A26531(this$, _arg) {
    return foldBack((cid, sheet) => {
        let option_3, option_1, matchValue;
        return defaultArg((option_3 = ((option_1 = tryFind(sheet, this$.DesignComponentsById), (option_1 != null) ? tryFind(cid, option_1) : undefined)), (option_3 != null) ? ((matchValue = option_3.Type, (matchValue.tag === 26) ? matchValue.fields[0].Name : undefined)) : undefined), sheet);
    }, _arg, this$.DesignTopSheet);
}

/**
 * The label an instance carries on the canvas above it: what the user drew, and what tells
 * two instances of one sheet apart when the selector offers a choice between them. None for
 * the top sheet, which is named after its sheet rather than labelled.
 */
export function SimulatedDesign__LabelOfInstance_1A26531(this$, instance) {
    const option_1 = SimulatedDesign__CustomOfInstance_1A26531(this$, instance);
    if (option_1 != null) {
        return option_1.Label;
    }
    else {
        return undefined;
    }
}

/**
 * The labels of the custom component instances a path passes through, ROOT FIRST - which is
 * the order a path is read in, and so the one place the innermost-first order is turned round.
 * 
 * The ancestors of an instance are the tails of its path, which is what makes this a walk
 * rather than a prefix per level. `missing` stands for an instance the design no longer holds.
 */
export function SimulatedDesign__LabelsOfInstance_Z47F38896(this$, _arg, missing) {
    const walk = (ap_1) => {
        if (!isEmpty(ap_1)) {
            return cons(defaultArg(SimulatedDesign__LabelOfInstance_1A26531(this$, ap_1), missing), walk(tail(ap_1)));
        }
        else {
            return empty();
        }
    };
    return reverse(walk(_arg));
}

/**
 * The instances of `sheet` directly inside `parent`, alphabetically by id - so the head is
 * what a selector node with nothing recorded about it shows.
 * 
 * Read off the custom components of the parent's own sheet: one of those IS an instance of
 * the sheet it names, and its id goes on the near end of the parent's path to name the
 * instance it introduces. One sheet's components, whatever the parent is an instance of.
 */
export function SimulatedDesign__InstancesInside_Z47F38896(this$, parent, sheet) {
    const parent_1 = parent;
    return sort(map_1((tupledArg_1) => cons(tupledArg_1[0], parent_1), filter((tupledArg) => {
        const matchValue = tupledArg[1].Type;
        if (matchValue.tag === 26) {
            return matchValue.fields[0].Name === sheet;
        }
        else {
            return false;
        }
    }, toList(defaultArg(tryFind(SimulatedDesign__SheetOfInstance_1A26531(this$, parent_1), this$.DesignComponentsById), empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }))))), {
        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
    });
}

/**
 * The design component one component of one instance is a copy of.
 * 
 * What it gives is the component as DRAWN. Its kind, its label and its .ram-file comments are
 * facts about that; its widths are not, since a parameterised sheet resolves those per
 * instance - for a width, ask `PortView` for the port.
 */
export function SimulatedDesign__ComponentOfInstance_6546966D(this$, _arg) {
    const option_1 = tryFind(SimulatedDesign__SheetOfInstance_1A26531(this$, _arg[1]), this$.DesignComponentsById);
    if (option_1 != null) {
        return tryFind(_arg[0], option_1);
    }
    else {
        return undefined;
    }
}

/**
 * The design-time sheet a component was drawn on, found by its id. None for an id this design
 * does not hold - one left over from a simulation of an earlier version of it.
 */
export function SimulatedDesign__SheetOfComponent_72DDFA37(this$, compId) {
    return tryPick((sheet, comps) => {
        if (containsKey(compId, comps)) {
            return sheet;
        }
        else {
            return undefined;
        }
    }, this$.DesignComponentsById);
}

/**
 * The sheets instantiated directly on one sheet, with the id of the custom component doing
 * the instantiating. One entry per custom component, so a sheet placed twice appears twice.
 */
export function SimulatedDesign__SubSheetsOf_Z721C83C5(this$, sheet) {
    return choose((tupledArg) => {
        const matchValue = tupledArg[1].Type;
        if (matchValue.tag === 26) {
            return [tupledArg[0], matchValue.fields[0].Name];
        }
        else {
            return undefined;
        }
    }, toList(defaultArg(tryFind(sheet, this$.DesignComponentsById), empty_1({
        Compare: (x, y) => (compare(x, y) | 0),
    }))));
}

function SimulatedDesign__get_SheetsParentsFirst(this$) {
    const order = (tupledArg, sheet) => {
        const seen = tupledArg[0];
        const acc = tupledArg[1];
        if (contains(sheet, seen)) {
            return [seen, acc];
        }
        else {
            const patternInput = fold(order, [add(sheet, seen), acc], List_distinct(map_1((tuple) => tuple[1], SimulatedDesign__SubSheetsOf_Z721C83C5(this$, sheet)), {
                Equals: (x, y) => (x === y),
                GetHashCode: (x) => (stringHash(x) | 0),
            }));
            return [patternInput[0], cons(sheet, patternInput[1])];
        }
    };
    return order([empty_2({
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }), empty()], this$.DesignTopSheet)[1];
}

/**
 * Which sheets hold a component `chosen` accepts, at any depth.
 * 
 * What `InstancesOfComponents` prunes with: a sheet outside this set contains none of what is
 * being looked for and nothing that contains any, so no instance of it need be visited.
 * Settled in one pass over sheets ordered so that each comes after everything inside it.
 */
export function SimulatedDesign__SheetsHolding_Z5E942382(this$, chosen) {
    return fold((holding, sheet) => {
        if (exists((_arg, comp) => chosen(comp), defaultArg(tryFind(sheet, this$.DesignComponentsById), empty_1({
            Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
        }))) ? true : exists_1((tupledArg) => contains(tupledArg[1], holding), SimulatedDesign__SubSheetsOf_Z721C83C5(this$, sheet))) {
            return add(sheet, holding);
        }
        else {
            return holding;
        }
    }, empty_2({
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), reverse(SimulatedDesign__get_SheetsParentsFirst(this$)));
}

/**
 * Every INSTANCE of every component the design draws that `chosen` accepts, as the component
 * as drawn and the instance it is in.
 * 
 * This is how a list the whole simulation contributes to - every memory in it, every viewer -
 * is worked out without the simulation. The cost is the design plus the instances that
 * actually hold one of these, because a subtree holding none is not descended into: a design
 * whose 49,152 sheet instances contain four memories costs the route to those four, not the
 * expansion. Walking the FastComponents instead cost every component of every instance -
 * 480,000 of them on main6 of largeTest - to find the same four.
 * 
 * In the order the design draws them: each sheet's own matches, by id, then the subsheets in
 * id order. Callers wanting something else sort.
 */
export function SimulatedDesign__InstancesOfComponents_Z5E942382(this$, chosen) {
    const holding = SimulatedDesign__SheetsHolding_Z5E942382(this$, chosen);
    const walk = (instance, sheet) => {
        const instance_1 = instance;
        return append(choose((tupledArg) => {
            const comp = tupledArg[1];
            if (chosen(comp)) {
                return [comp, instance_1];
            }
            else {
                return undefined;
            }
        }, toList(defaultArg(tryFind(sheet, this$.DesignComponentsById), empty_1({
            Compare: (x, y) => (compare(x, y) | 0),
        })))), collect((tupledArg_2) => walk(cons(tupledArg_2[0], instance_1), tupledArg_2[1]), filter((tupledArg_1) => contains(tupledArg_1[1], holding), sortBy((tuple) => tuple[0], SimulatedDesign__SubSheetsOf_Z721C83C5(this$, sheet), {
            Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
        }))));
    };
    if (contains(this$.DesignTopSheet, holding)) {
        return walk(empty(), this$.DesignTopSheet);
    }
    else {
        return empty();
    }
}

/**
 * The dot-separated component labels naming one component of one instance - the labels of the
 * custom components entered, then the component's own.
 * 
 * This is what `FastComponent.FullName` holds, and what the RAM selector lists RAMs under, so
 * it must stay the same string: the selection saved against it is keyed by it.
 */
export function SimulatedDesign__FullNameOf_Z7956074D(this$, comp, instance) {
    return StringModule_Concat(".", append(SimulatedDesign__LabelsOfInstance_Z47F38896(this$, instance, "*"), singleton(comp.Label)));
}

/**
 * How many instances of each sheet the design expands to.
 * 
 * Counted on the SHEET graph rather than by expanding it: a sheet appears once for every time
 * each of its parents appears, so one walk of the sheets somebody drew gives a number it
 * would take 49,152 instances to reach by enumeration. Sheets nothing instantiates are absent.
 * 
 * The sum is held below a ceiling so that a deep design cannot overflow it. Nothing asks the
 * count of a sheet with thousands of instances - what is asked is whether there is exactly
 * one - so a saturating count answers every question anyone puts to it.
 */
export function SimulatedDesign__get_SheetInstanceCounts(this$) {
    return fold((counts, sheet) => {
        const here = defaultArg(tryFind(sheet, counts), 0) | 0;
        return fold((counts_1, tupledArg) => change(tupledArg[1], (c) => min(1000000, defaultArg(c, 0) + here), counts_1), counts, SimulatedDesign__SubSheetsOf_Z721C83C5(this$, sheet));
    }, ofList(singleton([this$.DesignTopSheet, 1]), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    }), SimulatedDesign__get_SheetsParentsFirst(this$));
}

/**
 * How many components the design expands to - what the simulation holds, said without
 * holding one. One canvas component becomes one simulated component per instance of its
 * sheet, so the expansion count is per-sheet arithmetic over the instance counts, saturating
 * where they do.
 */
export function SimulatedDesign__get_ExpandedComponentCount(this$) {
    return fold_1((total, sheet, count) => {
        let option_1;
        return (total + (count * defaultArg((option_1 = tryFind(sheet, this$.DesignComponentsById), (option_1 != null) ? count_1(option_1) : undefined), 0))) | 0;
    }, 0, SimulatedDesign__get_SheetInstanceCounts(this$)) | 0;
}

/**
 * The one instance of a sheet, where the design has exactly one; None where it has none or
 * several.
 * 
 * Everything inside a sheet placed twice is itself there twice, so the sheets with a single
 * instance form a tree and the route down to one of them is unique. Following only those is
 * what keeps this the size of the design.
 */
export function SimulatedDesign__SoleInstanceOfSheet_Z721C83C5(this$, sheet) {
    const counts = SimulatedDesign__get_SheetInstanceCounts(this$);
    const find = (instance, current) => {
        const instance_1 = instance;
        if (current === sheet) {
            return instance_1;
        }
        else {
            return tryPick_1((tupledArg) => {
                const name = tupledArg[1];
                if (equals(tryFind(name, counts), 1)) {
                    return find(cons(tupledArg[0], instance_1), name);
                }
                else {
                    return undefined;
                }
            }, SimulatedDesign__SubSheetsOf_Z721C83C5(this$, current));
        }
    };
    if (!equals(tryFind(sheet, counts), 1)) {
        return undefined;
    }
    else {
        return find(empty(), this$.DesignTopSheet);
    }
}

/**
 * An instance path as a person reads it: the labels of the custom components entered, dot
 * separated, and the top sheet's own name for the instance nothing contains.
 * 
 * A rendering, not an identity - which is why it may be ambiguous without consequence. Two
 * instances can share a label path only if a label repeats on one canvas, and what decides
 * which wave is which is the path itself.
 */
export function SimulatedDesign__LabelPathOfInstance_1A26531(this$, instance) {
    const instance_1 = instance;
    if (isEmpty(instance_1)) {
        return this$.DesignTopSheet;
    }
    else {
        return StringModule_Concat(".", SimulatedDesign__LabelsOfInstance_Z47F38896(this$, instance_1, "?"));
    }
}

export const emptySimulatedDesign = new SimulatedDesign(empty(), empty(), "", empty_1({
    Compare: (x, y) => (comparePrimitives(x, y) | 0),
}), empty_1({
    Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
}));

/**
 * Whether a build makes the tables only a waveform VIEWER needs: WaveComps, Drivers and
 * WaveIndex.
 * 
 * Nothing that RUNS a simulation reads any of them - not the run loop, not the reducers, not
 * FastExtract, not RamView - so a simulator that only runs and is read over a wire can leave them
 * out. They are sized by the EXPANSION, one entry per port of every instance, which is exactly
 * what a remote simulator exists to keep out of the process that draws.
 * 
 * The custom-component port linking that used to be part of the same phase is not optional and
 * happens either way: it re-points a custom component's ports at the arrays of the Input and
 * Output components inside it, which is what makes those ports readable at all.
 */
export class WaveTables extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["WithWaveTables", "NoWaveTables"];
    }
    static WithWaveTables = new WaveTables(0, []);
    static NoWaveTables = new WaveTables(1, []);
}

export function WaveTables_$reflection() {
    return union_type("SimTypes.WaveTables", [], WaveTables, () => [[], []]);
}

/**
 * What one clock cycle of a design costs in step arrays, kept apart by which memory it comes from.
 * 
 * The two are not interchangeable, and measurably so - though not in the way performance.memory
 * suggests. usedJSHeapSize counts Uint32Arrays at every size, so it cannot tell the two apart;
 * what separates them is the LIMIT. Uint32Array allocation ran to 15.5GB on a 32GB machine
 * against a jsHeapSizeLimit of 3.7GB, so those are bounded by the machine and not by V8's pointer
 * compression cage. Buses wider than 32 bits are held as a plain array of BigInt, which is
 * ordinary heap - 400MB of values cost 454MB - inside that 4GB cage, shared with the model, the
 * design and the waveforms. So a design can be refused for the second while nowhere near the first.
 * FastCreate.stepBytesForWidth works out one port's share; FastCreate.stepCostOfGraph totals it.
 */
export class StepCost extends Record {
    constructor(TypedArrayBytes, HeapBytes) {
        super();
        this.TypedArrayBytes = (TypedArrayBytes | 0);
        this.HeapBytes = (HeapBytes | 0);
    }
}

export function StepCost_$reflection() {
    return record_type("SimTypes.StepCost", [], StepCost, () => [["TypedArrayBytes", int32_type], ["HeapBytes", int32_type]]);
}

export function StepCost__get_TotalBytes(this$) {
    return (this$.TypedArrayBytes + this$.HeapBytes) | 0;
}

export const SimulationBudget_typedArrayShareOfMachine = 0.33;

export const SimulationBudget_heapShareOfLimit = 0.35;

/**
 * What one component of an expanded design costs in heap, by how many ports it has (ports
 * here is inputs plus outputs, as GraphMerger.expandedSize counts them).
 * 
 * Measured rather than derived, on a hierarchy of 120,000 expanded components with one output
 * port each and 2.9 ports each in all, by GC-forced usedJSHeapSize deltas at two step-array
 * lengths (which separates the fixed cost from the per-step cost - the fixed part was the
 * same at both lengths to within noise). What one waveform simulation retains, per component:
 * 
 * the SimulationGraph            ~15 bytes - merger shares non-custom nodes between
 * instances of a sheet, so this is two orders of magnitude
 * down on the rest
 * the FastComponents            ~990 bytes fixed - the 25-field record, its map node, the
 * output IOArrays with their typed-array wrappers (~120 B a
 * wrapper, measured), path strings, reducer closures
 * the Wave records               ~430 bytes a wave, at ~0.6 waves per port when this was
 * measured - the Wave record's strings, built by the
 * waveform simulator only, but charged here because this
 * guard cannot know which simulator is coming and the
 * waveform one is both the heavier and the one large designs
 * are opened in
 * 
 * That last term is no longer spent. The waveform simulator described every wave the
 * simulation offered and kept the lot in its model; it now describes the waves of the sheet
 * instances the selector is drawing, as it draws them, and keeps records only for the ones
 * SELECTED - at most maxAllowedViewerWaves, which is a hundred. So about 260 of the 350 bytes
 * per port below is a cost that has gone, and this guard now refuses designs it has the memory
 * for. The number is left where it is because it was MEASURED and the replacement has not
 * been: subtracting a term from someone else's measurement is how a guard ends up on the wrong
 * side. Remeasure and lower it.
 * 
 * The step arrays themselves are NOT here - they scale with cycles, not components, and are
 * StepCost's business. The formula sits ~15% above the measured total at 2.9 ports, which is
 * the right side to miss on for a guard. To remeasure after changing these structures: build
 * phase deltas are logged under --log=perf (FastBuild.buildFastSimulation and the defaultWaves
 * line), and `node scripts/drive.js` + window.issieDev.simStats() gives the exact component,
 * port and wave counts to divide by.
 */
export function SimulationBudget_heapBytesPerComponent(ports) {
    return 1000 + (350 * ports);
}

export let SimulationBudget_maxTypedArrayBytes = createAtom(2000000000);

export let SimulationBudget_maxHeapBytes = createAtom(1000000000);

export const SimulationBudget_runtimeHeadroom = 1.5;

/**
 * Size both budgets to the machine this is running on. Called once from renderer startup.
 * 
 * physicalBytes comes from process.getSystemMemoryInfo, heapLimitBytes from
 * performance.memory.jsHeapSizeLimit - the limit actually in force, whatever Main.fs asked for
 * and whatever V8 decided to grant. Either being zero or absent leaves that budget at its
 * fallback, so a machine that cannot answer is never told it has no memory.
 */
export function SimulationBudget_setBudgetsFromMachine(physicalBytes, heapLimitBytes) {
    if (physicalBytes > 0) {
        SimulationBudget_maxTypedArrayBytes(physicalBytes * SimulationBudget_typedArrayShareOfMachine);
    }
    if (heapLimitBytes > 0) {
        SimulationBudget_maxHeapBytes(heapLimitBytes * SimulationBudget_heapShareOfLimit);
    }
}

/**
 * A size in bytes, written the way a message should read it.
 */
export function SimulationBudget_formatBytes(bytes) {
    const gb = bytes / Math.pow(1024, 3);
    const mb = bytes / Math.pow(1024, 2);
    if (gb >= 1) {
        return `${interpolate("%.1f%P()", [gb])} GB`;
    }
    else if (mb >= 1) {
        return `${interpolate("%.0f%P()", [mb])} MB`;
    }
    else {
        return `${interpolate("%.0f%P()", [bytes / 1024])} KB`;
    }
}

/**
 * What a run of a fast simulation did.
 * 
 * Not a rate. It used to return cycles per millisecond, with None meaning both "nothing to do" and
 * "finished" - so a caller could not tell those apart, and the one caller that used the number
 * divided by it to guess how long the rest would take. Guessing elapsed time from work done is
 * what breaks when a machine sleeps mid-run: the rate collapses towards zero and instant work is
 * predicted to take minutes. This says what happened and nothing more.
 */
export class RunOutcome extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["RunCompleted", "RunStoppedAt"];
    }
    static RunCompleted = new RunOutcome(0, []);
}

export function RunOutcome_$reflection() {
    return union_type("SimTypes.RunOutcome", [], RunOutcome, () => [[], [["clock", int32_type]]]);
}

export class FastSimulation extends Record {
    constructor(ClockTick, MaxArraySize, FGlobalInputComps, FConstantComps, FClockedComps, FOrderedComps, FIOActive, FIOLinks, FCompsByIndex, FIndexOf, FCustomOutputCompLookup, NumStepArrays, Drivers, WaveIndex, Design, StepCost) {
        super();
        this.ClockTick = (ClockTick | 0);
        this.MaxArraySize = (MaxArraySize | 0);
        this.FGlobalInputComps = FGlobalInputComps;
        this.FConstantComps = FConstantComps;
        this.FClockedComps = FClockedComps;
        this.FOrderedComps = FOrderedComps;
        this.FIOActive = FIOActive;
        this.FIOLinks = FIOLinks;
        this.FCompsByIndex = FCompsByIndex;
        this.FIndexOf = FIndexOf;
        this.FCustomOutputCompLookup = FCustomOutputCompLookup;
        this.NumStepArrays = (NumStepArrays | 0);
        this.Drivers = Drivers;
        this.WaveIndex = WaveIndex;
        this.Design = Design;
        this.StepCost = StepCost;
    }
}

export function FastSimulation_$reflection() {
    return record_type("SimTypes.FastSimulation", [], FastSimulation, () => [["ClockTick", int32_type], ["MaxArraySize", int32_type], ["FGlobalInputComps", array_type(FastComponent_$reflection())], ["FConstantComps", array_type(FastComponent_$reflection())], ["FClockedComps", array_type(FastComponent_$reflection())], ["FOrderedComps", array_type(FastComponent_$reflection())], ["FIOActive", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [tuple_type(string_type, list_type(int32_type)), FastComponent_$reflection()])], ["FIOLinks", list_type(tuple_type(tuple_type(FastComponent_$reflection(), int32_type), FastComponent_$reflection()))], ["FCompsByIndex", array_type(FastComponent_$reflection())], ["FIndexOf", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [tuple_type(int32_type, list_type(int32_type)), int32_type])], ["FCustomOutputCompLookup", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [tuple_type(tuple_type(int32_type, list_type(int32_type)), int32_type), int32_type])], ["NumStepArrays", int32_type], ["Drivers", array_type(option_type(Driver_$reflection()))], ["WaveIndex", array_type(WaveIndexT_$reflection())], ["Design", SimulatedDesign_$reflection()], ["StepCost", StepCost_$reflection()]]);
}

/**
 * Scaffolding for building a FastSimulation, and alive only while one is built.
 * 
 * It used to be four `Map`s: the flattened design indexed the several ways the phases after the
 * flatten needed it, all of them keyed structurally by (ComponentId, access path). Every one of
 * those keys cost a boxed comparison per tree level of every lookup, and the build does millions
 * of lookups - a measured fifth of a 480,000-component build went on one of them. They are now a
 * single index space instead: the flatten creates each FastComponent, stamps it with its position
 * in `Comps`, and expresses every link it finds as those indices. Nothing here is keyed by
 * anything but an int, and what a built simulation offers the rest of the program - the store in
 * gather order, and one map from a design-time name into it - is made once at the end, from this.
 * 
 * One store and one index space, holding the FastComponents themselves: custom against ordinary
 * is a PREDICATE over it, never a second store. Splitting them is the obvious tidy-up and it is
 * what would break this - the indices the links carry would then mean two different things.
 * 
 * Deliberately not kept after the build. It holds a SimulationComponent per component INSTANCE
 * through the FastComponents it stores, so on a large design it is one of the biggest things the
 * simulator ever allocates, and a FastSimulation left holding one made every later edit slower by
 * giving each major GC all of it to trace.
 */
export class GatherData extends Record {
    constructor(Comps, Labels) {
        super();
        this.Comps = Comps;
        this.Labels = Labels;
    }
}

export function GatherData_$reflection() {
    return record_type("SimTypes.GatherData", [], GatherData, () => [["Comps", LookupArray$1_$reflection(FastComponent_$reflection())], ["Labels", array_type(string_type)]]);
}

/**
 * Any component of the build, ordinary or custom.
 * 
 * This used to be a third map, WaveComps, holding the union of the two - built by folding one
 * into the other after every build, which on a design of 120,000 components is a copy of all
 * of them for a lookup that is two tries. It was also the only reason a wave viewer needed a
 * structure the wave tables built, so removing it is what lets the same code answer from a
 * build made without them.
 * The component at one index of this build. Not valid across builds: an index names a slot
 * in the store that made it, which is why what OUTLIVES a build is the design-time name.
 */
export function FastSimulation__ComponentAt_3B11D6BA(this$, fci) {
    return item(fcToInt(fci), this$.FCompsByIndex);
}

export function FastSimulation__ComponentOf_6546966D(this$, fId) {
    const option_1 = tryFind(fId, this$.FIndexOf);
    if (option_1 != null) {
        return FastSimulation__ComponentAt_3B11D6BA(this$, option_1);
    }
    else {
        return undefined;
    }
}

/**
 * The same, for a caller that knows the component is there and says so - the callers that
 * used to index WaveComps directly. Raises naming the id when it is not, which is what
 * indexing a map did.
 */
export function FastSimulation__FastComponentOf_6546966D(this$, fId) {
    const matchValue = FastSimulation__ComponentOf_6546966D(this$, fId);
    if (matchValue == null) {
        return toFail(`What? no component ${fId} in this simulation`);
    }
    else {
        return matchValue;
    }
}

/**
 * The design-sized fields, under the names they had when they were fields of this record.
 * They read through to `Design`, which is where they live now - so that the design can be
 * held, and asked, without a simulation to hang it on.
 */
export function FastSimulation__get_SimulatedCanvasState(this$) {
    return this$.Design.DesignSheets;
}

export function FastSimulation__get_SimulatedTopSheet(this$) {
    return this$.Design.DesignTopSheet;
}

export function FastSimulation__get_ComponentsById(this$) {
    return this$.Design.DesignComponentsById;
}

export function FastSimulation__get_ConnectionsByPort(this$) {
    return this$.Design.DesignConnectionsByPort;
}

/**
 * The custom component an instance is the innards of, or None for the top sheet.
 */
export function FastSimulation__parentCustomOf_1A26531(this$, instance) {
    return SimulatedDesign__CustomOfInstance_1A26531(this$.Design, instance);
}

/**
 * The design-time name of the sheet an instance is of: what the user called it, and what they
 * see in the Sheets menu. An instance is a path; this says which sheet it is a copy of.
 */
export function FastSimulation__getSheetNameOfInstance_1A26531(this$, instance) {
    return SimulatedDesign__SheetOfInstance_1A26531(this$.Design, instance);
}

/**
 * The label of one design component, or "*" for an id the design does not have.
 */
export function GatherData__labelOf_72DDFA37(this$, _arg) {
    const i = _arg;
    if ((i >= 0) && (i < this$.Labels.length)) {
        return item(i, this$.Labels);
    }
    else {
        return "*";
    }
}

/**
 * human readable dot-separated name of component in simulation.
 * This uses the component labels to the root of the simulation and therefore is unique.
 */
export function GatherData__getFullSimName_6546966D(this$, _arg) {
    return StringModule_Concat(".", map_1((cid_1) => GatherData__labelOf_72DDFA37(this$, cid_1), reverse(cons(_arg[0], _arg[1]))));
}

/**
 * - Top level data tracking a simulation
 */
export class SimulationData extends Record {
    constructor(FastSim, Graph, Inputs, Outputs, IsSynchronous, NumberBase, ClockTickNumber) {
        super();
        this.FastSim = FastSim;
        this.Graph = Graph;
        this.Inputs = Inputs;
        this.Outputs = Outputs;
        this.IsSynchronous = IsSynchronous;
        this.NumberBase = NumberBase;
        this.ClockTickNumber = (ClockTickNumber | 0);
    }
}

export function SimulationData_$reflection() {
    return record_type("SimTypes.SimulationData", [], SimulationData, () => [["FastSim", FastSimulation_$reflection()], ["Graph", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, SimulationComponent_$reflection()])], ["Inputs", list_type(tuple_type(int32_type, string_type, int32_type))], ["Outputs", list_type(tuple_type(int32_type, string_type, int32_type))], ["IsSynchronous", bool_type], ["NumberBase", NumberBase_$reflection()], ["ClockTickNumber", int32_type]]);
}

export const graph_ = [(a) => a.Graph, (s) => ((a_1) => (new SimulationData(a_1.FastSim, s, a_1.Inputs, a_1.Outputs, a_1.IsSynchronous, a_1.NumberBase, a_1.ClockTickNumber)))];

export const fastSim_ = [(a) => a.FastSim, (s) => ((a_1) => (new SimulationData(s, a_1.Graph, a_1.Inputs, a_1.Outputs, a_1.IsSynchronous, a_1.NumberBase, a_1.ClockTickNumber)))];

export const numberBase_ = [(a) => a.NumberBase, (s) => ((a_1) => (new SimulationData(a_1.FastSim, a_1.Graph, a_1.Inputs, a_1.Outputs, a_1.IsSynchronous, s, a_1.ClockTickNumber)))];

export const clockTickNumber_ = [(a) => (a.ClockTickNumber | 0), (s) => ((a_1) => (new SimulationData(a_1.FastSim, a_1.Graph, a_1.Inputs, a_1.Outputs, a_1.IsSynchronous, a_1.NumberBase, s)))];

/**
 * document current status of a simulation as used by waveform simulator
 */
export class SimulationRunStatus extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["SimEmpty", "SimOutOfDate", "SimValidSameSheet", "SimValidDifferentSheet", "SimNoProject"];
    }
    static SimEmpty = new SimulationRunStatus(0, []);
    static SimOutOfDate = new SimulationRunStatus(1, []);
    static SimValidSameSheet = new SimulationRunStatus(2, []);
    static SimValidDifferentSheet = new SimulationRunStatus(3, []);
    static SimNoProject = new SimulationRunStatus(4, []);
}

export function SimulationRunStatus_$reflection() {
    return union_type("SimTypes.SimulationRunStatus", [], SimulationRunStatus, () => [[], [], [], [], []]);
}

export function sprintSimComponent(sComp) {
    const arg_1 = sprintInitial(20, toString_1(sComp.Type));
    return toText(printf("\'%A\': %20s"))(sComp.Label)(arg_1);
}

export function shortPSComp(comp) {
    const lab = comp.Label;
    const matchValue_1 = comp.Type;
    if (matchValue_1.tag === 26) {
        const sc = matchValue_1.fields[0];
        return toText(printf("%s:Custom.(%s.%A->%A)"))(lab)(sc.Name)(sc.InputLabels)(sc.OutputLabels);
    }
    else {
        return toText(printf("%s:%A"))(lab)(comp.Type);
    }
}

export function tryGetCompLabel(compId, sg) {
    let option_3, option_1;
    return defaultArg((option_3 = ((option_1 = SimGraph_tryFind(compId, sg), (option_1 != null) ? option_1.Label : undefined)), (option_3 != null) ? option_3 : undefined), "\'Not in SimGraph\'");
}

export function extractLabel(label) {
    return label;
}

export function mapKeys(map) {
    return MapModule_KeysA(map);
}

export function mapValues(map) {
    return MapModule_ValuesA(map);
}

export function mapItems(map) {
    return toArray(map);
}

